import { BASIC_COURSE_SOURCE_ALIASES } from "../basicCourseSourcePolicy";
import { OFFICIAL_MATURA_SHEET_MODULES } from "../official/officialMaturaSheetRegistry";

const SOURCE_MONTHS = Object.freeze({
  STY: "styczeń",
  LUT: "luty",
  MAR: "marzec",
  KWI: "kwiecień",
  MAJ: "maj",
  CZE: "czerwiec",
  LIP: "lipiec",
  SIE: "sierpień",
  WRZ: "wrzesień",
  PAZ: "październik",
  LIS: "listopad",
  GRU: "grudzień",
});

const SHEET_MONTH_CODES = Object.freeze({
  maj: "MAJ",
  czerwiec: "CZE",
  sierpien: "SIE",
  marzec: "MAR",
  grudzien: "GRU",
});

function inferSession(sheetId) {
  const match = /^cke-p-(\d{4})-([a-z]+)$/.exec(String(sheetId || ""));
  if (!match) return "";
  const monthCode = SHEET_MONTH_CODES[match[2]];
  return monthCode ? `${match[1]}_${monthCode}` : "";
}

function buildSourcesByTaskType() {
  const sourcesByTaskType = Object.create(null);

  Object.entries(OFFICIAL_MATURA_SHEET_MODULES).forEach(([sheetId, sheetModule]) => {
    sheetModule.generatePagedTasks("original").forEach((entry) => {
      const taskType = entry.task?.type;
      const taskNo = String(entry.meta?.taskNo ?? entry.task?.source?.taskNumber ?? "");
      if (!taskType || !taskNo) return;

      const source = Object.freeze({
        taskType,
        sheetId,
        taskNo,
        exam: entry.task?.source?.exam || "MATURA",
        session: entry.task?.source?.session || inferSession(sheetId),
        points: entry.meta?.maxPoints || entry.task?.data?.maxPoints || 1,
      });

      if (!sourcesByTaskType[taskType]) sourcesByTaskType[taskType] = [];
      sourcesByTaskType[taskType].push(source);
    });
  });

  Object.keys(sourcesByTaskType).forEach((taskType) => {
    sourcesByTaskType[taskType].sort((a, b) => {
      const date = source => {
        const [year, month] = source.session.split("_");
        return Number(year) * 100 + Object.keys(SOURCE_MONTHS).indexOf(month) + 1;
      };
      return date(b) - date(a) || a.taskNo.localeCompare(b.taskNo, "pl", { numeric: true });
    });
    sourcesByTaskType[taskType] = Object.freeze(sourcesByTaskType[taskType]);
  });
  return Object.freeze(sourcesByTaskType);
}

// Rejestr jest wyliczany z oficjalnych arkuszy. Dodanie zadania do arkusza
// automatycznie udostępnia jego źródło we wszystkich runnerach.
// Sheet modules also import source-link components. Resolve the registry only
// after module initialization so direct entry to the catalog is safe.
let sourcesByTaskType;
function getSourcesByTaskType() {
  if (!sourcesByTaskType) sourcesByTaskType = buildSourcesByTaskType();
  return sourcesByTaskType;
}

// Niektóre widoki kursu pokazują wydzieloną część większego zadania CKE pod
// osobnym typem generatora. Nadal powinny prowadzić do tego samego oryginału.
const SOURCE_TASK_TYPE_ALIASES = BASIC_COURSE_SOURCE_ALIASES;

export function getMaturaOriginalTaskSources(taskType) {
  const sourceTaskType = SOURCE_TASK_TYPE_ALIASES[taskType] || taskType;
  return getSourcesByTaskType()[sourceTaskType] || [];
}

export function getMaturaOriginalTaskSource(sheetId, taskNo) {
  const normalizedTaskNo = String(taskNo ?? "");
  return Object.values(getSourcesByTaskType())
    .flat()
    .find((source) => (
      source.sheetId === sheetId && String(source.taskNo) === normalizedTaskNo
    )) || null;
}

export function formatMaturaOriginalSource(source, { includeTask = true } = {}) {
  if (!source) return "";
  const parts = String(source.session || "").split("_");
  const year = parts.find((part) => /^\d{4}$/.test(part)) || "";
  const monthCode = parts.find((part) => SOURCE_MONTHS[part]);
  const date = [monthCode ? SOURCE_MONTHS[monthCode] : "", year]
    .filter(Boolean)
    .join(" ");
  return [date, includeTask ? `zadanie ${source.taskNo}` : ""]
    .filter(Boolean)
    .join(", ");
}

export function getMaturaOriginalTaskPath(source) {
  if (!source) return "";
  return `/matura/zadania-oryginalne/${encodeURIComponent(source.sheetId)}/${encodeURIComponent(source.taskNo)}`;
}
